import { getAllReleaseNotes, getUnreadNotes } from '@/services/releaseNotes'
import { formatDateWithFormatter } from '@/utils/function'
import { Sparkles, Gift } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

interface SidebarReleaseNoteProps {
    expanded: boolean
}

/**
 * Compact release note indicator for sidebar bottom
 * Shows latest version with animated effects when unread
 */
const SidebarReleaseNote = ({ expanded }: SidebarReleaseNoteProps) => {
    const [latestNote, setLatestNote] = useState<{
        version: string
        title: string
        date: string
    } | null>(null)
    const [unreadCount, setUnreadCount] = useState(0)

    useEffect(() => {
        const notes = getAllReleaseNotes()
        const unread = getUnreadNotes()

        if (notes.length > 0) {
            setLatestNote({
                version: notes[0].version,
                title: notes[0].title,
                date: notes[0].date,
            })
        }
        setUnreadCount(unread.length)

        // Check every 5 minutes
        const interval = setInterval(
            () => {
                setUnreadCount(getUnreadNotes().length)
            },
            5 * 60 * 1000
        )

        return () => clearInterval(interval)
    }, [])

    if (!latestNote) return null

    const hasUnread = unreadCount > 0

    const content = (
        <Link
            to="/release-notes"
            className={`relative flex items-center gap-3 p-2 rounded-md transition-all duration-300 group ${
                expanded ? 'justify-start' : 'justify-center'
            } ${
                hasUnread
                    ? 'bg-gradient-to-r from-blue-500/10 via-primary/10 to-cyan-500/10 hover:from-blue-500/20 hover:via-primary/20 hover:to-cyan-500/20'
                    : 'hover:bg-gray-100'
            }`}
        >
            {/* Animated ring when unread */}
            {hasUnread && (
                <div className="absolute inset-0 rounded-md overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-primary/20 to-cyan-500/20 animate-pulse" />
                </div>
            )}

            <div className="relative">
                {/* Glow effect when unread */}
                {hasUnread && (
                    <div className="absolute inset-0 w-9 h-9 -m-0.5 rounded-md bg-gradient-to-r from-blue-500 via-primary to-cyan-500 blur-md opacity-40 animate-pulse" />
                )}

                <div
                    className={`relative w-9 h-9 rounded-md flex items-center justify-center transition-all duration-300 ${
                        hasUnread
                            ? 'bg-gradient-to-br from-blue-500 via-primary to-cyan-500 shadow-lg shadow-primary/30'
                            : 'bg-primary/10 group-hover:bg-primary/20'
                    }`}
                >
                    {hasUnread ? (
                        <Gift className="w-4 h-4 text-white animate-bounce" />
                    ) : (
                        <Sparkles className="w-4 h-4 text-primary" />
                    )}
                </div>

                {/* Badge */}
                {hasUnread && (
                    <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-[10px] font-bold text-white flex items-center justify-center px-1.5 shadow-lg shadow-red-500/30 animate-bounce">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </div>

            {expanded && (
                <div className="relative flex-1 min-w-0">
                    {hasUnread ? (
                        <>
                            <p className="text-xs font-semibold bg-gradient-to-r from-blue-600 via-primary to-cyan-600 bg-clip-text text-transparent">
                                {unreadCount} cập nhật mới!
                            </p>
                            <p className="text-[10px] text-primary/70 truncate">
                                v{latestNote.version} •{' '}
                                {formatDateWithFormatter(
                                    latestNote.date,
                                    'DD/MM'
                                )}
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-xs font-medium text-gray-700 truncate">
                                v{latestNote.version}
                            </p>
                            <p className="text-[10px] text-gray-400 truncate">
                                {formatDateWithFormatter(
                                    latestNote.date,
                                    'DD/MM/YYYY'
                                )}
                            </p>
                        </>
                    )}
                </div>
            )}
        </Link>
    )

    // Wrap with tooltip when collapsed
    if (!expanded) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{content}</TooltipTrigger>
                    <TooltipContent
                        side="right"
                        className={`max-w-[200px] ${hasUnread ? 'bg-gradient-to-r from-blue-500 via-primary to-cyan-500 text-white border-0' : ''}`}
                    >
                        {hasUnread ? (
                            <>
                                <p className="font-semibold">
                                    {unreadCount} cập nhật mới!
                                </p>
                                <p className="text-xs text-white/80 mt-1">
                                    Click để xem chi tiết
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="font-medium">
                                    v{latestNote.version}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {formatDateWithFormatter(
                                        latestNote.date,
                                        'DD/MM/YYYY'
                                    )}
                                </p>
                            </>
                        )}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    }

    return content
}

export default SidebarReleaseNote
